import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import {
  ReadyForPaymentEvent,
  StartPaymentEvent,
  StartPaymentFailedEvent
} from 'components/ordering/checkout/events.js'

import {
  CLOUD_SUITE_STRIPE_BACKEND,
  CloudSuiteStripeBackend,
  FetchStripePaymentIntentRequest,
  StripePaymentIntent,
} from 'components/ordering/checkout/stripe/cloudsuite-stripe-backend.js'
import { StripeStartPaymentError } from 'components/ordering/checkout/stripe/third-party/start-payment-error-parser.js'
import {
  THIRD_PARTY_STRIPE_API,
  ThirdPartyStripeApi,
} from 'components/ordering/checkout/stripe/third-party/third-party-stripe-api.js'
import { CSSResultGroup, html, LitElement, TemplateResult } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { is } from 'typia'
import { stripeStyle } from './stripe.style.js'


const isSomething = <T>(maybe: T | undefined | null): maybe is T => maybe !== null && maybe !== undefined

const STRIPE_SLOT_NAME = 'stripe-payment-element'


@customElement('csc-stripe')
export class CSCStripe extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, stripeStyle]

  @property({ type: String, attribute: 'start-payment-intent-path' })
  private _startPaymentIntentPath?: string
  private get startPaymentIntentPath() {
    if (isSomething(this._startPaymentIntentPath)) { return this._startPaymentIntentPath }
    throw new Error('csc-stripe seems to be missing attribute "start-payment-intent-path"')
  }

  private _paymentIntent?: StripePaymentIntent
  private get paymentIntent(): StripePaymentIntent {
    if (isSomething(this._paymentIntent)) { return this._paymentIntent }
    throw new Error('StripePaymentIntent unknown, is it properly initialized?')
  }
  private set paymentIntent(value: StripePaymentIntent) {
    this._paymentIntent = value
  }

  @inject(CLOUD_SUITE_STRIPE_BACKEND)
  private cloudSuiteStripeBackend!: CloudSuiteStripeBackend

  @inject(THIRD_PARTY_STRIPE_API)
  private thirdPartyStripeAPI!: ThirdPartyStripeApi

  private initializeThirdPartyStripe = async () => {
    this.paymentIntent = await this.cloudSuiteStripeBackend.fetchPaymentIntent(
      new FetchStripePaymentIntentRequest(this.startPaymentIntentPath),
    )

    const stripeMounting = document.createElement('div')
    stripeMounting.slot = STRIPE_SLOT_NAME
    this.appendChild(stripeMounting)
    const wrapperElement = document.createElement('div')
    wrapperElement.classList.add('StripeElementWrapper')
    stripeMounting.appendChild(wrapperElement)

    await this.thirdPartyStripeAPI.initialize(this.paymentIntent)
    this.initializePaymentElement(wrapperElement)
    this.initializeExpressCheckoutElement(wrapperElement)
  }

  private initializeExpressCheckoutElement(wrapperElement: HTMLDivElement): void {
    if (!this.paymentIntent.renderExpressCheckout) { return }

    const expressCheckoutDiv = document.createElement('div')
    wrapperElement.appendChild(expressCheckoutDiv)
    this.thirdPartyStripeAPI.initializeExpressCheckoutElement(this.paymentIntent, expressCheckoutDiv)
  }

  private initializePaymentElement(wrapperElement: HTMLDivElement): void {
    const paymentElementDiv = document.createElement('div')
    wrapperElement.appendChild(paymentElementDiv)
    this.thirdPartyStripeAPI.initializePaymentElement(this.paymentIntent, paymentElementDiv, this.dispatchReadyForPayment)
  }

  private startPayment = async () => {
    const error = await this.thirdPartyStripeAPI.startPayment(this.paymentIntent.returnUrl)

    if (is<StripeStartPaymentError>(error)) {
      this.dispatchEvent(new StartPaymentFailedEvent(error.message))
    }
  }

  private dispatchReadyForPayment = () => {
    this.dispatchEvent(new ReadyForPaymentEvent())
  }

  override async connectedCallback() {
    super.connectedCallback()

    await this.initializeThirdPartyStripe()
    window.addEventListener(StartPaymentEvent.type, this.startPayment)
  }

  override disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener(StartPaymentEvent.type, this.startPayment)
  }

  protected override render(): TemplateResult {
    return html`
      <slot name="${STRIPE_SLOT_NAME}">
        <span>Payment methods are loading...</span>
      </slot>
    `
  }
}
